import {
  ref,
  get,
  set,
  push,
  update,
  remove,
  runTransaction,
  serverTimestamp,
  query,
  orderByChild,
  equalTo,
  onValue,
  off,
  DataSnapshot,
} from 'firebase/database';
import { rtdb } from './config';

export {
  ref,
  get,
  set,
  push,
  update,
  remove,
  runTransaction,
  serverTimestamp,
  query,
  orderByChild,
  equalTo,
  onValue,
  off,
  rtdb,
};
export type { DataSnapshot };

// ─── Path Helpers ─────────────────────────────────────────────────────────────

export const usersRef          = ()                               => ref(rtdb, 'users');
export const userRef           = (uid: string)                   => ref(rtdb, `users/${uid}`);

export const companiesRef      = ()                               => ref(rtdb, 'companies');
export const companyRef        = (companyId: string)             => ref(rtdb, `companies/${companyId}`);

export const customersRef      = (companyId: string)             => ref(rtdb, `companies/${companyId}/customers`);
export const customerRef       = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/customers/${id}`);

export const productsRef       = (companyId: string)             => ref(rtdb, `companies/${companyId}/products`);
export const productRef        = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/products/${id}`);

export const invoicesRef       = (companyId: string)             => ref(rtdb, `companies/${companyId}/invoices`);
export const invoiceRef        = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/invoices/${id}`);

export const paymentsRef       = (companyId: string)             => ref(rtdb, `companies/${companyId}/payments`);
export const paymentRef        = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/payments/${id}`);

export const rolesRef          = (companyId: string)             => ref(rtdb, `companies/${companyId}/roles`);
export const roleRef           = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/roles/${id}`);

export const auditLogsRef      = (companyId: string)             => ref(rtdb, `companies/${companyId}/auditLogs`);
export const auditLogRef       = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/auditLogs/${id}`);

export const settingsRef       = (companyId: string)             => ref(rtdb, `companies/${companyId}/settings`);
export const invoiceSettingRef = (companyId: string)             => ref(rtdb, `companies/${companyId}/settings/invoice`);

export const templatesRef      = (companyId: string)             => ref(rtdb, `companies/${companyId}/invoiceTemplates`);
export const templateRef       = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/invoiceTemplates/${id}`);

export const unitsRef          = (companyId: string)             => ref(rtdb, `companies/${companyId}/settings/units`);
export const notificationsRef  = (companyId: string)             => ref(rtdb, `companies/${companyId}/notifications`);
export const notificationRef   = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/notifications/${id}`);

export const rateListRef         = (companyId: string)             => ref(rtdb, `companies/${companyId}/rateList`);
export const rateListItemsRef    = (companyId: string)             => ref(rtdb, `companies/${companyId}/rateList/items`);
export const rateListItemRef     = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/rateList/items/${id}`);
export const rateListSectionsRef = (companyId: string)             => ref(rtdb, `companies/${companyId}/rateList/sections`);
export const rateListSettingsRef = (companyId: string)             => ref(rtdb, `companies/${companyId}/rateList/settings`);
export const rateListsRef        = (companyId: string)             => ref(rtdb, `companies/${companyId}/rateLists`);
export const rateListDocRef      = (companyId: string, id: string) => ref(rtdb, `companies/${companyId}/rateLists/${id}`);
export const publicRateListRef   = ()                              => ref(rtdb, 'publicRateList');
export const publicRateListsRef  = ()                              => ref(rtdb, 'publicRateLists');
export const publicRateListDocRef= (id: string)                    => ref(rtdb, `publicRateLists/${id}`);

// ─── Data Normalization Utilities ─────────────────────────────────────────────

export interface DBTimestampObject {
  toDate: () => Date;
  valueOf: () => number;
  toString: () => string;
  seconds: number;
  nanoseconds: number;
}

export const normalizeDate = (val: any): DBTimestampObject | null => {
  if (val === undefined || val === null || val === '') return null;
  if (typeof val?.toDate === 'function') return val;
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return null;
  const ms = d.getTime();
  return {
    toDate: () => d,
    valueOf: () => ms,
    toString: () => d.toISOString(),
    seconds: Math.floor(ms / 1000),
    nanoseconds: (ms % 1000) * 1000000,
  };
};

export const hydrateDates = <T extends Record<string, any>>(item: T, dateFields: string[]): T => {
  if (!item) return item;
  const copy: any = { ...item };
  for (const field of dateFields) {
    if (copy[field] !== undefined) {
      copy[field] = normalizeDate(copy[field]);
    }
  }
  return copy as T;
};

export const snapshotToArray = <T extends Record<string, any>>(
  snapshot: DataSnapshot,
  dateFields: string[] = ['createdAt', 'updatedAt', 'invoiceDate', 'dueDate', 'paymentDate', 'timestamp', 'lastActive']
): T[] => {
  if (!snapshot.exists()) return [];
  const list: T[] = [];
  snapshot.forEach((child) => {
    const val = child.val();
    if (val && typeof val === 'object') {
      const item = { id: child.key, ...val };
      list.push(hydrateDates(item, dateFields) as T);
    }
  });
  return list;
};

export const snapshotToItem = <T extends Record<string, any>>(
  snapshot: DataSnapshot,
  dateFields: string[] = ['createdAt', 'updatedAt', 'invoiceDate', 'dueDate', 'paymentDate', 'timestamp', 'lastActive']
): T | null => {
  if (!snapshot.exists()) return null;
  const val = snapshot.val();
  if (!val || typeof val !== 'object') return null;
  const item = { id: snapshot.key, ...val };
  return hydrateDates(item, dateFields) as T;
};
